package module

import com.tencent.kuikly.core.render.web.export.KuiklyRenderBaseModule
import com.tencent.kuikly.core.render.web.ktx.KuiklyRenderCallback
import com.tencent.kuikly.core.render.web.ktx.toJSONObjectSafely
import com.tencent.kuikly.core.render.web.nvi.serialization.json.JSONObject
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.json

/**
 * H5 端支付拉起模块（非小程序浏览器环境）。
 *
 * 策略：
 * - 微信 H5 支付 (channel=2)：读 unified.h5.payUrl / unified.h5.mwebUrl ，新窗口或当前窗口跳转
 * - 支付宝 H5 支付 (channel=1)：读 unified.h5.payUrl / unified.alipay.h5Url，同样跳转
 * - 没有命中 H5 URL 时返回 ERR_NOT_IMPL，业务层可提示用户扫码或切换其他端支付
 *
 * 返回 { success, errCode, errorMsg, payUrl? }
 */
class KRPayLauncherModule : KuiklyRenderBaseModule() {

    override fun call(method: String, params: String?, callback: KuiklyRenderCallback?): Any? {
        return when (method) {
            "launch" -> {
                launch(params, callback)
                null
            }
            else -> {
                callback?.invoke(makeJson(success = false, errCode = ERR_UNSUPPORTED, errorMsg = "unknown method: $method"))
                null
            }
        }
    }

    private fun launch(params: String?, callback: KuiklyRenderCallback?) {
        try {
            val root = (params ?: "{}").toJSONObjectSafely()
            val channel = root.optInt("channel", 0)
            val unified = root.optJSONObject("unified") ?: JSONObject("{}")

            // 先尝试 h5.* 下的 URL，兼容常见后端字段命名
            val h5 = unified.optJSONObject("h5")

            var url: String? = h5?.let {
                it.optString("payUrl").ifBlank { null }
                    ?: it.optString("mwebUrl").ifBlank { null }
                    ?: it.optString("redirect_url").ifBlank { null }
            }

            // 支付宝: 可能 h5Url 在 alipay 子对象下
            if (url == null && channel == CHANNEL_ALIPAY) {
                val alipay = unified.optJSONObject("alipay")
                val orderOrUrl = alipay?.let {
                    it.optString("h5Url").ifBlank { null }
                        ?: it.optString("payUrl").ifBlank { null }
                        ?: it.optString("orderStr").ifBlank { null }
                }
                val gateway = alipay?.optString("gateway").orEmpty()

                if (!orderOrUrl.isNullOrBlank() && !orderOrUrl.startsWith("http", ignoreCase = true) &&
                    gateway.isNotBlank()) {
                    // orderStr 不是 url，直接 submit a form with action 到 alipay gateway
                    submitForm(gateway, mapOf("biz_content" to orderOrUrl))
                    callback?.invoke(makeJson(success = true, errCode = 0, errorMsg = "ok", payUrl = gateway))
                    return
                }
                url = orderOrUrl
            }

            if (url == null) {
                callback?.invoke(makeJson(success = false, errCode = ERR_NOT_IMPL, errorMsg = "H5 payUrl not found in unified response"))
                return
            }

            // 新窗口跳转，避免 Kuikly 页面被冲掉
            val tab = window.open(url, "_blank")
            if (tab == null) {
                // 弹窗被拦截，尝试当前页面跳转
                window.location.href = url
            }
            callback?.invoke(makeJson(success = true, errCode = 0, errorMsg = "redirected", payUrl = url))
        } catch (t: Throwable) {
            callback?.invoke(makeJson(success = false, errCode = ERR_NOT_IMPL, errorMsg = (t.message ?: "h5 pay launch exception")))
        }
    }

    private fun submitForm(action: String, fields: Map<String, String>) {
        val form = document.createElement("form").asDynamic()
        form.method = "post"
        form.action = action
        form.target = "_blank"
        form.acceptCharset = "UTF-8"
        fields.forEach { (k, v) ->
            val input = document.createElement("input").asDynamic()
            input.type = "hidden"
            input.name = k
            input.value = v
            form.appendChild(input)
        }
        document.body?.appendChild(form)
        form.submit()
        document.body?.removeChild(form)
    }

    private fun makeJson(
        success: Boolean,
        errCode: Int,
        errorMsg: String,
        payUrl: String? = null
    ): String {
        val map = json(
            "success" to success,
            "errCode" to errCode,
            "errorMsg" to errorMsg
        )
        if (payUrl != null) map["payUrl"] = payUrl
        return JSON.stringify(map)
    }

    companion object {
        const val MODULE_NAME = "KRPayLauncherModule"

        // 与 PaymentApi.CHANNEL_* 约定一致
        private const val CHANNEL_ALIPAY = 1

        // 与 commonMain PayLauncherModule 保持一致
        private const val ERR_UNSUPPORTED = -4
        private const val ERR_NOT_IMPL = -99
    }
}
